/** @module app.test */

import { ITestSession } from 'app/test/types';

type Constructor<T extends Object = any> = new (...args: any[]) => T;

export interface IAssertErrorOptions {
    type?: string;
    expected?: string;
    operation?: string;
    actual?: string;
    details?: string;
}

export class AssertError extends Error {

    public type: string;
    public expected: string;
    public operation: string;
    public actual: string;
    public details: string;

    constructor(message?: string, options: IAssertErrorOptions = {}) {
        super(message || 'Assertion Failed');
        Object.setPrototypeOf(this, AssertError.prototype);

        this.type = options.type || 'default';
        this.expected = options.expected || '';
        this.operation = options.operation || '';
        this.actual = options.actual || '';
        this.details = options.details || '';
    }

}

export interface IAssertion {
    expected?: any;
    operation?: string;
    actual?: any;
    details?: string;
}

type Coercable<T> = { valueOf(): T; }

export class Assert {

    constructor(private readonly session?: ITestSession) { }

    public fail(details: string): this {
        this.count();
        throw this.error('fail', { details });
        //return this;
    }

    public areEqual<T>(expected: T, actual: T, details?: string): this {
        this.count();
        if(expected !== actual) {
            throw this.error('areEqual', { operation: '===', expected, actual, details });
        }
        return this;
    }

    public areNotEqual<T>(expected: T, actual: T, details?: string): this {
        this.count();
        if(expected === actual) {
            throw this.error('areNotEqual', { operation: '!==', expected, actual, details });
        }
        return this;
    }

    public areLooselyEqual<T>(expected: T | Coercable<T>, actual: T | Coercable<T>, details?: string): this {
        this.count();
        if(expected != actual) {
            throw this.error('areLooselyEqual', { operation: '==', expected, actual, details });
        }
        return this;
    }

    public areNotLooselyEqual<T>(expected: T | Coercable<T>, actual: T | Coercable<T>, details?: string): this {
        this.count();
        if(expected == actual) {
            throw this.error('areNotLooselyEqual', { operation: '!=', expected, actual, details });
        }
        return this;
    }

    public areSame<T>(expected: T, actual: T, details?: string): this {
        this.count();
        if(!Object.is(expected, actual)) {
            throw this.error('areSame', { operation: 'is', expected, actual, details });
        }
        return this;
    }

    public areNotSame<T>(expected: T, actual: T, details?: string): this {
        this.count();
        if(Object.is(expected, actual)) {
            throw this.error('areNotSame', { operation: 'is not', expected, actual, details });
        }
        return this;
    }

    public isTrue(value: unknown, details?: string): this {
        this.count();
        if(value !== true) {
            throw this.error('isTrue', { actual: value, details });
        }
        return this;
    }

    public isNotTrue(value: unknown, details?: string): this {
        this.count();
        if(value === true) {
            throw this.error('isNotTrue', { actual: value, details });
        }
        return this;
    }

    public isTruthy(value: unknown, details?: string): this {
        this.count();
        if(!value) {
            throw this.error('isTruthy', { actual: value, details });
        }
        return this;
    }

    public isFalse(value: unknown, details?: string): this {
        this.count();
        if(value !== false) {
            throw this.error('isFalse', { actual: value, details });
        }
        return this;
    }

    public isNotFalse(value: unknown, details?: string): this {
        this.count();
        if(value === false) {
            throw this.error('isNotFalse', { actual: value, details });
        }
        return this;
    }

    public isFalsy(value: unknown, details?: string): this {
        this.count();
        if(!!value) {
            throw this.error('isFalsy', { actual: value, details });
        }
        return this;
    }

    public isNull(value: unknown, details?: string): this {
        this.count();
        if(value !== null) {
            throw this.error('isNull', { actual: value, details });
        }
        return this;
    }

    public isNotNull(value: unknown, details?: string): this {
        this.count();
        if(value === null) {
            throw this.error('isNotNull', { actual: value, details });
        }
        return this;
    }

    public isUndefined(value: unknown, details?: string): this {
        this.count();
        if(value !== undefined) {
            throw this.error('isUndefined', { actual: value, details });
        }
        return this;
    }

    public isNotUndefined(value: unknown, details?: string): this {
        this.count();
        if(value === undefined) {
            throw this.error('isNotUndefined', { actual: value, details });
        }
        return this;
    }

    public isBoolean(value: unknown, details?: string): this {
        this.count();
        if(typeof value !== 'boolean') {
            throw this.error('isBoolean', { actual: value, details });
        }
        return this;
    }

    public isNotBoolean(value: unknown, details?: string): this {
        this.count();
        if(typeof value === 'boolean') {
            throw this.error('isNotBoolean', { actual: value, details });
        }
        return this;
    }

    public isNumber(value: unknown, details?: string): this {
        this.count();
        if(typeof value !== 'number') {
            throw this.error('isNumber', { actual: value, details });
        }
        return this;
    }

    public isNotNumber(value: unknown, details?: string): this {
        this.count();
        if(typeof value === 'number') {
            throw this.error('isNotNumber', { actual: value, details });
        }
        return this;
    }

    public isNaN(value: unknown, details?: string): this {
        this.count();
        if(typeof value !== 'number' || !Number.isNaN(value)) {
            throw this.error('isNaN', { actual: value, details });
        }
        return this;
    }

    public isNotNaN(value: unknown, details?: string): this {
        this.count();
        if(typeof value === 'number' && Number.isNaN(value)) {
            throw this.error('isNotNaN', { actual: value, details });
        }
        return this;
    }

    public isFiniteNumber(value: unknown, details?: string): this {
        this.count();
        if(typeof value !== 'number' || !Number.isFinite(value)) {
            throw this.error('isFiniteNumber', { actual: value, details });
        }
        return this;
    }

    public isNotFiniteNumber(value: unknown, details?: string): this {
        this.count();
        if(typeof value === 'number' && Number.isFinite(value)) {
            throw this.error('isNotFiniteNumber', { actual: value, details });
        }
        return this;
    }

    public isString(value: unknown, details?: string): this {
        this.count();
        if(typeof value !== 'string') {
            throw this.error('isString', { actual: value, details });
        }
        return this;
    }

    public isNotString(value: unknown, details?: string): this {
        this.count();
        if(typeof value === 'string') {
            throw this.error('isNotString', { actual: value, details });
        }
        return this;
    }

    public isArray(value: unknown, details?: string): this {
        this.count();
        if(!Array.isArray(value)) {
            throw this.error('isArray', { actual: value, details });
        }
        return this;
    }

    public isNotArray(value: unknown, details?: string): this {
        this.count();
        if(Array.isArray(value)) {
            throw this.error('isNotArray', { actual: value, details });
        }
        return this;
    }

    public isEmpty(value: string | any[], details?: string): this {
        this.count();
        if(Array.isArray(value) ? value.length !== 0 : value !== '') {
            throw this.error('isEmpty', { actual: value, details });
        }
        return this;
    }

    public isNotEmpty(value: string | any[], details?: string): this {
        this.count();
        if(Array.isArray(value) ? value.length === 0 : value === '') {
            throw this.error('isNotEmpty', { actual: value, details });
        }
        return this;
    }

    public isInstance<T>(value: unknown, constructor: Constructor<T>, details?: string): this {
        this.count();
        if(!(value instanceof constructor)) {
            throw this.error('isInstance', { operation: 'instanceof', actual: value, expected: constructor, details });
        }
        return this;
    }

    public isNotInstance<T>(value: unknown, constructor: Constructor<T>, details?: string): this {
        this.count();
        if(value instanceof constructor) {
            throw this.error('isNotInstance', { operation: 'not instanceof', actual: value, expected: constructor, details });
        }
        return this;
    }

    public stringContains(value: string | null | undefined, find: string, details?: string): this {
        this.count();
        if(!value || value.indexOf(find) < 0) {
            throw this.error('stringContains', { actual: value, details });
        }
        return this;
    }

    public stringNotContains(value: string | null | undefined, find: string, details?: string): this {
        this.count();
        if(value && value.indexOf(find) >= 0) {
            throw this.error('stringNotContains', { actual: value, details });
        }
        return this;
    }

    public hasProperty(value: any, property: string | number | symbol, details?: string): this {
        this.count();
        if(!value || !(property in value)) {
            throw this.error('hasProperty', { actual: value, details });
        }
        return this;
    }

    public hasNoProperty(value: any, property: string | number | symbol, details?: string): this {
        this.count();
        if(value && (property in value)) {
            throw this.error('hasNoProperty', { actual: value, details });
        }
        return this;
    }

    protected count(): void {
        if(this.session) {
            this.session.assertions++;
        }
    }

    protected format(value: any): string {
        // TODO: support more special cases
        if(value === undefined) { return 'undefined' }
        if(value === null) { return 'null'; }
        if(value instanceof Function) { return value.name; }
        return JSON.stringify(value);
    }

    protected error(type: string, assertion: IAssertion): AssertError {
        const options: IAssertErrorOptions = {
            type: type,
            expected: 'expected' in assertion ? this.format(assertion.expected) : '',
            operation: assertion.operation,
            actual: 'actual' in assertion ? this.format(assertion.actual) : '',
            details: assertion.details
        };

        let message: string = '';
        if(options.operation && options.expected && options.actual) {
            message = `${type}: ${options.actual} ${options.operation} ${options.expected}`;
        }
        else if(options.actual) {
            message = `${type}: ${options.actual}`;
        }
        else {
            message = `${type}: Assertion Failed`;
        }
        if(options.details) {
            message = `${message} (${options.details})`;
        }

        return new AssertError(message, options);
    }

}
